import React from "react";
import { MapPin, Phone, Mail, Globe, Building2, MessageCircle } from "lucide-react";
import { PageHero, PageBody } from "./_shared";
import { useRoe } from "./RoeLayout";
import { instituteHref, isExternalUrl } from "@/lib/instituteUrl";
import InstituteMap from "@/components/InstituteMap";

export default function Contact() {
  const data = useRoe();
  const inst = data.institute || {};

  const address = [inst.address1, inst.address2, inst.city, inst.district, inst.state, inst.pincode]
    .filter(Boolean)
    .join(", ");
  const phones = [...new Set([inst.admission_phone, inst.contact_phone].filter(Boolean))];

  return (
    <div data-testid="roe-contact">
      <PageHero eyebrow="Get in Touch" title="Contact Us" accent="blue" />
      <PageBody>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 bg-white border border-slate-200 overflow-hidden" data-testid="roe-contact-card">
            <div className="bg-[#001F3F] text-white p-6 border-b-4 border-[#D4A017]">
              <div className="w-10 h-10 bg-white/10 flex items-center justify-center mb-3">
                <Building2 size={20} strokeWidth={1.5} />
              </div>
              <h3 className="font-extrabold text-xl tracking-tight">{inst.name || "Institute"}</h3>
              {inst.code && (
                <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#D4A017] font-semibold">
                  Institute Code · {inst.code}
                </div>
              )}
            </div>
            <div className="p-6 space-y-4 text-sm">
              {address && (
                <div className="flex items-start gap-3" data-testid="roe-contact-address">
                  <MapPin size={16} strokeWidth={1.5} className="text-[#D4A017] mt-0.5 shrink-0" />
                  <span className="text-slate-700">{address}</span>
                </div>
              )}
              {phones.length > 0 && (
                <div className="flex items-start gap-3" data-testid="roe-contact-phones">
                  <Phone size={16} strokeWidth={1.5} className="text-[#D4A017] mt-0.5 shrink-0" />
                  <span className="text-slate-700">
                    {phones.map((p, i) => (
                      <React.Fragment key={`${p}-${i}`}>
                        <a href={`tel:${p.replace(/\s+/g, "")}`} className="text-[#001F3F] hover:underline">
                          {p}
                        </a>
                        {i < phones.length - 1 && " · "}
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              )}
              {inst.whatsapp_phone && (
                <div className="flex items-start gap-3" data-testid="roe-contact-whatsapp">
                  <MessageCircle size={16} strokeWidth={1.5} className="text-[#D4A017] mt-0.5 shrink-0" />
                  <a
                    href={`https://wa.me/${inst.whatsapp_phone.replace(/[^0-9]/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#001F3F] hover:underline"
                  >
                    WhatsApp · {inst.whatsapp_phone}
                  </a>
                </div>
              )}
              {inst.email && (
                <div className="flex items-start gap-3" data-testid="roe-contact-email">
                  <Mail size={16} strokeWidth={1.5} className="text-[#D4A017] mt-0.5 shrink-0" />
                  <a href={`mailto:${inst.email}`} className="text-[#001F3F] hover:underline break-all">
                    {inst.email}
                  </a>
                </div>
              )}
              {inst.website_slug && (
                <div className="flex items-start gap-3" data-testid="roe-contact-website">
                  <Globe size={16} strokeWidth={1.5} className="text-[#D4A017] mt-0.5 shrink-0" />
                  <a
                    href={isExternalUrl(inst.website_slug) ? inst.website_slug : `https://gsmandal.org${instituteHref(inst.website_slug)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#001F3F] hover:underline"
                  >
                    {isExternalUrl(inst.website_slug)
                      ? inst.website_slug.replace(/^https?:\/\//i, "")
                      : `gsmandal.org/${inst.website_slug}`}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Quick actions card */}
          <div className="bg-[#001F3F] text-white p-6 flex flex-col gap-4 border-t-4 border-[#D4A017]" data-testid="roe-contact-actions">
            <h3 className="font-extrabold text-xl">Quick Actions</h3>
            <p className="text-sm text-white/80">Reach the admissions team directly or send a WhatsApp message.</p>
            <div className="mt-2 flex flex-col gap-2">
              {inst.admission_phone && (
                <a
                  href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`}
                  className="inline-flex items-center justify-center gap-2 bg-[#D4A017] hover:bg-[#E6B800] text-[#001F3F] font-bold uppercase tracking-[0.1em] text-[12px] px-4 py-3"
                  data-testid="roe-contact-action-call"
                >
                  <Phone size={14} strokeWidth={2} /> Call Admissions
                </a>
              )}
              {inst.email && (
                <a
                  href={`mailto:${inst.email}`}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-white/90 text-[#001F3F] font-bold uppercase tracking-[0.1em] text-[12px] px-4 py-3"
                  data-testid="roe-contact-action-email"
                >
                  <Mail size={14} strokeWidth={2} /> Email Us
                </a>
              )}
              {inst.whatsapp_phone && (
                <a
                  href={`https://wa.me/${inst.whatsapp_phone.replace(/[^0-9]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold uppercase tracking-[0.1em] text-[12px] px-4 py-3"
                  data-testid="roe-contact-action-whatsapp"
                >
                  <MessageCircle size={14} strokeWidth={2} /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#D4A017] font-bold mb-3">Find us on map</div>
          <InstituteMap
            latitude={inst.latitude}
            longitude={inst.longitude}
            address={address}
            testid="roe-contact-map"
            className="w-full aspect-[16/9] overflow-hidden border-2 border-[#D4A017] bg-[#001F3F]/5"
          />
        </div>
      </PageBody>
    </div>
  );
}
